'use client';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  fairtradeDarkControlClass,
  fairtradeDarkMenuClass,
} from '@/components/help-center/fairtrade/fairtrade-dark-panel';
import {
  FAIRTRADE_SORT_OPTIONS,
  type FairtradeSortId,
} from '@/lib/fairtrade-query';
import { cn } from '@/lib/utils';

type FairtradeSortSelectProps = {
  value: FairtradeSortId;
  onChange: (sort: FairtradeSortId) => void;
  isDark?: boolean;
};

const darkTriggerClass = cn(
  fairtradeDarkControlClass,
  'h-9 w-full min-w-[11.5rem] gap-2 px-3 font-inter text-sm !text-blue-50 sm:w-auto',
  '[&>span]:line-clamp-1 [&>span]:text-left',
  'data-[placeholder]:!text-blue-200/40',
  '[&_svg]:!text-sky-300/80 [&_svg]:opacity-100'
);

const lightTriggerClass = cn(
  'flex h-9 w-full min-w-[11.5rem] items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3',
  'font-inter text-sm text-slate-800 shadow-sm outline-none',
  'hover:border-sky-300 focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20',
  'sm:w-auto [&>span]:line-clamp-1 [&>span]:text-left'
);

const darkContentClass = cn(
  fairtradeDarkMenuClass,
  'z-[80] max-h-72 w-[var(--radix-select-trigger-width)] overflow-hidden p-0 text-blue-50',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  'data-[side=bottom]:slide-in-from-top-2'
);

const darkItemClass = cn(
  'relative flex w-full cursor-pointer select-none items-center rounded-lg py-2 pl-8 pr-3',
  'font-inter text-sm text-blue-100/90 outline-none',
  'focus:bg-sky-400/15 focus:text-sky-50',
  'data-[highlighted]:bg-sky-400/15 data-[highlighted]:text-sky-50',
  'data-[state=checked]:text-sky-100'
);

const lightItemClass = cn(
  'relative flex w-full cursor-pointer select-none items-center rounded-lg py-2 pl-8 pr-3',
  'font-inter text-sm text-slate-700 outline-none',
  'focus:bg-sky-50 focus:text-sky-900',
  'data-[highlighted]:bg-sky-50 data-[highlighted]:text-sky-900'
);

export default function FairtradeSortSelect({
  value,
  onChange,
  isDark = true,
}: FairtradeSortSelectProps) {
  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor="fairtrade-sort"
        className={cn(
          'hidden font-poppins text-[11px] font-semibold uppercase tracking-wide sm:inline',
          isDark ? '!text-sky-200/60' : '!text-slate-500'
        )}
      >
        Ordenação
      </label>
      <Select value={value} onValueChange={(v) => onChange(v as FairtradeSortId)}>
        <SelectTrigger
          id="fairtrade-sort"
          className={isDark ? darkTriggerClass : lightTriggerClass}
        >
          <SelectValue placeholder="Ordenação" />
        </SelectTrigger>
        <SelectContent
          side="bottom"
          sideOffset={6}
          position="popper"
          avoidCollisions={false}
          className={cn(
            isDark
              ? darkContentClass
              : 'z-[80] max-h-72 overflow-hidden rounded-xl border border-slate-200 bg-white p-0 text-slate-800 shadow-lg',
            'p-1.5'
          )}
        >
          {FAIRTRADE_SORT_OPTIONS.map((opt) => (
            <SelectItem
              key={opt.id}
              value={opt.id}
              className={isDark ? darkItemClass : lightItemClass}
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
